import IconButton from '@/components/ui/custom/buttons/IconButton';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { formatFileSize, formatFileType } from '@/utils/file';
import { AstroFile } from '@/models/AstroFile';
import { DocumentIcon, VideoCameraIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import ConditionalRenderer from '../ConditionalRenderer';

type FileCardProps = {
  file: AstroFile;
  onRemove?: (e: React.MouseEvent) => void;
  url?: string;
  className?: string;
};

const getIconByFileType = (fileType: string | undefined) => {
  const icons = {
    PDF: DocumentIcon,
    MP4: VideoCameraIcon,
    MOV: VideoCameraIcon,
    AVI: VideoCameraIcon,
    WMV: VideoCameraIcon,
    WEBM: VideoCameraIcon,
    FLV: VideoCameraIcon,
    MPEG: VideoCameraIcon,
    default: DocumentIcon,
  };

  fileType = formatFileType(fileType || '');
  return icons[fileType as keyof typeof icons] || icons.default;
};

export default function FileCard({
  file,
  onRemove,
  url,
  className,
}: FileCardProps) {
  const getFileTypeFromFileName = (fileName: string): string => {
    const extension = fileName.split('.').pop() || '';
    return extension.toUpperCase();
  };

  const fileType =
    file.file instanceof File
      ? file.file.type
      : getFileTypeFromFileName(file.fileName);

  const fileSize = file.file instanceof File ? file.file.size || 0 : undefined;

  const Icon = getIconByFileType(fileType);

  const fileSizeText = fileSize !== undefined ? formatFileSize(fileSize) : '';
  const fileInfoText = fileSizeText
    ? `${formatFileType(fileType)} • ${fileSizeText}`
    : formatFileType(fileType);

  return (
    <ConditionalWrapper
      condition={!!url}
      wrapper={<Link to={url || ''} target="_blank" className="!w-fit" />}
    >
      <div
        className={twMerge(
          'relative bg-base-100 border border-neutral-content min-h-16 rounded-xl flex items-center pl-4 pr-8 py-2 gap-2.5 w-full',
          className,
        )}
      >
        <Icon className="w-10 text-primary" />
        <div className="flex flex-col gap-1">
          <Text text={file.fileName.split('.').at(0)} />
          <Text text={fileInfoText} className="!text-14 text-base-content/80" />
        </div>
        <ConditionalRenderer condition={!!onRemove}>
          <IconButton
            color="custom"
            className="text-primary absolute right-2 top-2 p-1 rounded-full bg-neutral-content"
            icon={<XMarkIcon className="w-3 h-3" />}
            onClick={onRemove}
          />
        </ConditionalRenderer>
      </div>
    </ConditionalWrapper>
  );
}
